import React from 'react';
import { FileText } from 'lucide-react';
import LandingNav from '../components/Landing-Nav.js';
import FooterLanding from '../components/FooterLanding.js';

const TermsAndConditions = () => {
  return (
    <>
    <LandingNav/>
    <div className="font-inter bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 min-h-screen">
      <div className="max-w-5xl mx-auto bg-white p-8 md:p-12 rounded-2xl shadow-2xl">
        <div className="text-center mb-12">
          <FileText className="w-16 h-16 text-blue-600 mx-auto mb-4" />
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Terms and Conditions</h1>
          <p className="text-lg text-gray-600">Last Updated: January 2025</p>
        </div>

        <div className="prose prose-lg max-w-none">
          <p className="text-xl text-gray-700 leading-relaxed mb-8 font-semibold">
            Welcome to Shiftry! These Terms and Conditions ("Terms") constitute a legally binding agreement between you and Manocore Technologies Private Limited ("Manocore," "we," "us," or "our") governing your use of the Shiftry workforce management platform (the "Service").
          </p>

          <div className="p-6 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg mb-8">
            <p className="text-gray-800 font-semibold">
              By accessing or using the Service, you agree to be bound by these Terms. If you do not agree to these Terms, do not use the Service.
            </p>
          </div>

          {/* Section 1: Definitions */}
          <section className="mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-l-4 border-blue-600 pl-4">1. Definitions</h2>
            <ul className="list-disc list-inside space-y-3 mb-6 text-gray-700 ml-6">
              <li><strong>"Service"</strong> refers to the Shiftry web and mobile applications, software, APIs, features, and related services for workforce management</li>
              <li><strong>"User" or "You"</strong> refers to any individual or entity using the Service, including administrators, managers, and employees</li>
              <li><strong>"Account"</strong> means the registered account created to access the Service</li>
              <li><strong>"Organization"</strong> refers to the legal entity (company, business) subscribing to Shiftry</li>
              <li><strong>"Your Data"</strong> includes all information, content, and data submitted to the Service by you or your authorized users</li>
              <li><strong>"Subscription"</strong> refers to the paid service plan (Monthly, Annual, or Enterprise) selected during registration</li>
            </ul>
          </section>

          {/* Section 2: Service Description */}
          <section className="mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-l-4 border-blue-600 pl-4">2. Service Description and Scope</h2>
            <p className="mb-4 text-gray-700 leading-relaxed">
              Shiftry provides enterprise-grade workforce management capabilities including:
            </p>
            <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700 ml-6">
              <li>Employee scheduling and shift management</li>
              <li>GPS-enabled time and attendance tracking</li>
              <li>Leave and absence management</li>
              <li>Team communication and messaging</li>
              <li>Analytics, reporting, and Employee cost insights</li>
              <li>AI-powered schedule optimization</li>
              <li>Mobile applications (iOS and Android)</li>
              <li>API access for integrations</li>
            </ul>
            <p className="mb-6 text-gray-700 leading-relaxed">
              We reserve the right to modify, enhance, or discontinue features with reasonable notice.
            </p>
          </section>

          {/* Section 3: Account Registration */}
          <section className="mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-l-4 border-blue-600 pl-4">3. Account Registration and Security</h2>
            
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">3.1 Account Creation</h3>
            <p className="mb-6 text-gray-700 leading-relaxed">
              You must provide accurate, current, and complete information during registration. If using the Service on behalf of an Organization, you represent that you have authority to bind that entity to these Terms.
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-4">3.2 Account Security</h3>
            <p className="mb-4 text-gray-700 leading-relaxed">
              You are responsible for:
            </p>
            <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700 ml-6">
              <li>Maintaining confidentiality of login credentials</li>
              <li>All activities occurring under your Account</li>
              <li>Implementing multi-factor authentication (MFA) when available</li>
              <li>Immediately notifying us of unauthorized access at security@manocore.com</li>
            </ul>

            <h3 className="text-2xl font-semibold text-gray-800 mb-4">3.3 Account Suspension</h3>
            <p className="mb-6 text-gray-700 leading-relaxed">
              We reserve the right to suspend or terminate accounts that violate these Terms, engage in fraudulent activity, or pose security risks.
            </p>
          </section>

          {/* Section 4: Subscription and Payment */}
          <section className="mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-l-4 border-blue-600 pl-4">4. Subscription, Payment, and Billing</h2>
            
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">4.1 Subscription Plans</h3>
            <p className="mb-4 text-gray-700 leading-relaxed">
              Shiftry offers the following subscription tiers:
            </p>
            <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700 ml-6">
              <li><strong>Monthly Plan:</strong> Billed monthly, auto-renews every 30 days</li>
              <li><strong>Annual Plan:</strong> Billed yearly, auto-renews annually (discounted rate)</li>
              <li><strong>Enterprise Plan:</strong> Custom pricing and terms negotiated separately</li>
            </ul>

            <h3 className="text-2xl font-semibold text-gray-800 mb-4">4.2 Pricing and Fees</h3>
            <p className="mb-6 text-gray-700 leading-relaxed">
              Pricing is based on the number of active users (employees) in your Organization. Current pricing is available at www.shiftry.com/pricing. We reserve the right to adjust pricing with 30 days' notice to existing customers.
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-4">4.3 Payment Processing</h3>
            <p className="mb-6 text-gray-700 leading-relaxed">
              All payments are processed securely through Razorpay. By subscribing, you authorize Razorpay to charge your payment method for recurring fees. We do not store full credit card information.
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-4">4.4 Auto-Renewal</h3>
            <p className="mb-6 text-gray-700 leading-relaxed">
              Subscriptions automatically renew unless canceled before the renewal date. You will receive email notification 7 days before renewal. Cancellation takes effect at the end of the current billing period.
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-4">4.5 Failed Payments</h3>
            <p className="mb-6 text-gray-700 leading-relaxed">
              If payment fails, we will attempt to charge your payment method up to 3 times over 10 days. If unsuccessful, your account may be suspended until payment is received.
            </p>
          </section>

          {/* Section 5: Cancellation Policy */}
          <section className="mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-l-4 border-blue-600 pl-4">5. Cancellation and Refund Policy</h2>
            
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">5.1 Cancel Anytime</h3>
            <p className="mb-6 text-gray-700 leading-relaxed">
              <strong>You may cancel your subscription at any time with no penalties, fees, or questions asked.</strong> To cancel:
            </p>
            <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700 ml-6">
              <li>Navigate to Settings → Billing → Cancel Subscription</li>
              <li>Email support@manocore.com with "Cancellation Request" in the subject line</li>
              <li>Contact support via in-app chat</li>
            </ul>

            <h3 className="text-2xl font-semibold text-gray-800 mb-4">5.2 Effective Date</h3>
            <p className="mb-6 text-gray-700 leading-relaxed">
              Cancellation takes effect at the end of your current billing period. You retain full access until that date. No refunds are provided for partial billing periods.
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-4">5.3 Refund Policy</h3>
            <p className="mb-6 text-gray-700 leading-relaxed">
              We offer a <strong>14-day money-back guarantee</strong> for first-time subscribers. If unsatisfied, request a full refund within 14 days of initial purchase by emailing support@manocore.com. Refunds for subsequent renewals are issued at our discretion.
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-4">5.4 Data Export Before Cancellation</h3>
            <p className="mb-6 text-gray-700 leading-relaxed">
              Before canceling, export your data via Settings → Data Export. Data is retained for 90 days post-cancellation, after which it is permanently deleted.
            </p>
          </section>

          {/* Section 6: Data Ownership and Usage */}
          <section className="mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-l-4 border-blue-600 pl-4">6. Data Ownership and Usage Rights</h2>
            
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">6.1 Your Data Ownership</h3>
            <p className="mb-6 text-gray-700 leading-relaxed">
              You retain all ownership rights to Your Data. Manocore does not claim ownership of any content, schedules, employee records, or other data you upload to the Service.
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-4">6.2 License Grant</h3>
            <p className="mb-6 text-gray-700 leading-relaxed">
              You grant Manocore a limited, non-exclusive license to process, store, and display Your Data solely to provide the Service and improve platform features.
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-4">6.3 Data Responsibility</h3>
            <p className="mb-6 text-gray-700 leading-relaxed">
              You are responsible for ensuring that Your Data complies with applicable laws, including employment regulations, data protection laws (GDPR, CCPA), and does not infringe third-party rights.
            </p>
          </section>

          {/* Section 7: Service Level Agreement */}
          <section className="mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-l-4 border-blue-600 pl-4">7. Service Level Agreement (SLA)</h2>
            
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">7.1 Uptime Commitment</h3>
            <p className="mb-6 text-gray-700 leading-relaxed">
              We guarantee 99.9% uptime for Enterprise plans, calculated monthly excluding scheduled maintenance. Monthly plans receive 99.5% uptime guarantee.
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-4">7.2 Scheduled Maintenance</h3>
            <p className="mb-6 text-gray-700 leading-relaxed">
              We perform scheduled maintenance during off-peak hours (typically Sunday 2:00-4:00 AM IST). We provide 48-hour advance notice for planned downtime.
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-4">7.3 Support Response Times</h3>
            <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700 ml-6">
              <li><strong>Enterprise:</strong> 1-hour response for critical issues, 4-hour for standard</li>
              <li><strong>Annual:</strong> 4-hour response for critical, 24-hour for standard</li>
              <li><strong>Monthly:</strong> 24-hour response for all inquiries</li>
            </ul>
          </section>

          {/* Section 8: Acceptable Use */}
          <section className="mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-l-4 border-blue-600 pl-4">8. Acceptable Use Policy</h2>
            <p className="mb-4 text-gray-700 leading-relaxed">
              You agree NOT to:
            </p>
            <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700 ml-6">
              <li>Violate laws or regulations</li>
              <li>Infringe intellectual property rights</li>
              <li>Upload malicious code, viruses, or malware</li>
              <li>Attempt unauthorized access to systems or other accounts</li>
              <li>Reverse engineer, decompile, or disassemble the Service</li>
              <li>Use the Service for illegal employee surveillance</li>
              <li>Resell or redistribute the Service without authorization</li>
              <li>Send spam or unsolicited communications through our platform</li>
            </ul>
          </section>

          {/* Section 9: Limitation of Liability */}
          <section className="mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-l-4 border-blue-600 pl-4">9. Limitation of Liability</h2>
            <p className="mb-6 text-gray-700 leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, MANOCORE SHALL NOT BE LIABLE FOR:
            </p>
            <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700 ml-6">
              <li>Indirect, incidental, special, consequential, or punitive damages</li>
              <li>Loss of profits, revenue, data, or business opportunities</li>
              <li>Service interruptions or data loss</li>
              <li>Third-party actions or content</li>
            </ul>
            <p className="mb-6 text-gray-700 leading-relaxed">
              Our total liability shall not exceed the amount paid by you in the 12 months preceding the claim.
            </p>
          </section>

          {/* Section 10: Intellectual Property */}
          <section className="mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-l-4 border-blue-600 pl-4">10. Intellectual Property Rights</h2>
            <p className="mb-6 text-gray-700 leading-relaxed">
              The Service, including software, design, trademarks, logos, and documentation, is owned by Manocore and protected by intellectual property laws. You receive a limited license to use the Service; you do not acquire ownership rights.
            </p>
          </section>

          {/* Section 11: Dispute Resolution */}
          <section className="mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-l-4 border-blue-600 pl-4">11. Dispute Resolution and Governing Law</h2>
            
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">11.1 Governing Law</h3>
            <p className="mb-6 text-gray-700 leading-relaxed">
              These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of courts in Chennai, Tamil Nadu, India.
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-4">11.2 Arbitration</h3>
            <p className="mb-6 text-gray-700 leading-relaxed">
              Disputes exceeding ₹5,00,000 (INR) or $10,000 (USD) may be resolved through binding arbitration under Indian Arbitration and Conciliation Act, 1996.
            </p>
          </section>

          {/* Section 12: Changes to Terms */}
          <section className="mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-l-4 border-blue-600 pl-4">12. Modifications to Terms</h2>
            <p className="mb-6 text-gray-700 leading-relaxed">
              We may update these Terms periodically. Material changes will be communicated via email 30 days before taking effect. Continued use after changes constitutes acceptance.
            </p>
          </section>

          {/* Contact Section */}
          <section className="mb-8 p-6 bg-blue-50 rounded-xl border border-blue-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Contact Information</h2>
            <p className="mb-4 text-gray-700 leading-relaxed">
              For questions regarding these Terms:
            </p>
            <div className="space-y-2 text-gray-700">
              <p><strong>Email:</strong> legal@manocore.com</p>
              <p><strong>Support:</strong> support@manocore.com</p>
              <p><strong>Address:</strong> Manocore Technologies Private Limited, Chennai, Tamil Nadu, India</p>
            </div>
          </section>

          <div className="text-center pt-8 border-t-2 border-gray-200">
            <p className="text-sm text-gray-600">
              © 2026 Manocore Technologies. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
    <FooterLanding/>
    </>
  );
};

export default TermsAndConditions;
